import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import {
  DependencyAction,
  DependencyEffect,
  DependencyProps,
  DependencySourceType,
} from '../../../core/IDependency';
import { URIParameterContext } from '../../../core/contexte/URIParameterContext';
import DataSchemaContext from '../../../core/validation/dataSchemaContext';
import {
  evaluateCondition,
  getUrlParameterValue,
} from './conditionalFieldUtils';
import { FormConditionalFieldProps } from './types';
import { useExternalFieldsConfig } from '../../../core/contexte/externalFieldsConfigContext';

export const FormConditionalField: FunctionComponent<
  FormConditionalFieldProps
> = ({ children, fieldName, rulesConfig, elements, setParentVisible }) => {
  const { values, initialValues } = useFormState();
  const { mutators } = useForm();
  const { setExternalFields } = useExternalFieldsConfig();
  const {
    overrideValidationSchema,
    validationSchema,
    errorSchema,
    overrideErrorSchema,
  } = useContext(DataSchemaContext);
  const [fieldNamesToPrefill, setFieldNamesToPrefill] = useState<Set<string>>(
    new Set()
  );
  const [superiorValue, setSuperiorValue] = useState<any>(undefined);
  const [shouldRenderChildren, setShouldRenderChildren] =
    useState<boolean>(true);
  const { URIParameter } = useContext(URIParameterContext);

  const addFieldNameToPrefill = (value?: string): void => {
    if (!value) return;
    if (fieldNamesToPrefill.has(value)) return;
    if (initialValues[value] === undefined) return;
    setFieldNamesToPrefill((prev: Set<string>) => new Set(prev.add(value)));
  };

  const prefillAndRemoveFieldName = (value?: string): void => {
    if (!value) return;
    mutators.setValue(value, initialValues[value], values);
    setFieldNamesToPrefill((prev: Set<string>) => {
      prev.delete(value);
      return new Set(prev);
    });
  };

  const doAction = (
    action: DependencyAction,
    superiorValue: any,
    dependencySourceType: DependencySourceType | string
  ) => {
    switch (action.effect) {
      case DependencyEffect.SHOW:
        if (
          values[fieldName] === undefined &&
          initialValues[fieldName] &&
          fieldNamesToPrefill.has(fieldName)
        ) {
          prefillAndRemoveFieldName(fieldName);
        } else if (elements?.length) {
          elements
            .filter(
              (el) =>
                el.fieldName &&
                initialValues[el.fieldName] &&
                values[el?.fieldName] === undefined &&
                (initialValues[el.fieldName] ||
                  el.refComponent === 'components/@material/timeComponent')
            )
            .map((el) => el.fieldName)
            .forEach((fieldNameToSetInitValue) => {
              if (
                fieldNameToSetInitValue &&
                fieldNamesToPrefill.has(fieldNameToSetInitValue)
              )
                prefillAndRemoveFieldName(fieldNameToSetInitValue);
            });
        }
        setShouldRenderChildren(true);
        setParentVisible?.(true);
        return children;

      case DependencyEffect.HIDE:
        if (initialValues[fieldName] !== undefined)
          addFieldNameToPrefill(fieldName);
        if (values[fieldName]) {
          mutators.setValue(fieldName, undefined, values);
        } else if (elements?.length) {
          elements
            .filter((el) => el.fieldName)
            .map((el) => el.fieldName)
            .forEach((fieldNameToCleanUp) => {
              if (!fieldNameToCleanUp) return;
              if (values[fieldNameToCleanUp] !== undefined)
                mutators.setValue(fieldNameToCleanUp, undefined, values);
              addFieldNameToPrefill(fieldNameToCleanUp);
            });
        }
        if (
          dependencySourceType &&
          dependencySourceType === DependencySourceType.URL_PARAMETER
        ) {
          removeFieldFromValidation(fieldName);
          removeFieldFromErrorSchema(fieldName);
        }

        setShouldRenderChildren(false);
        setParentVisible?.(false);
        return null;

      default:
        setShouldRenderChildren(true); //default fall back for all rules if no action is specified
        setParentVisible?.(true);
        return children;
    }
  };

  const removeFieldFromValidation = (fieldName: string) => {
    if (validationSchema.properties && validationSchema.properties[fieldName]) {
      delete validationSchema.properties[fieldName];

      if (Array.isArray(validationSchema.required)) {
        validationSchema.required = validationSchema.required.filter(
          (r: string) => r !== fieldName
        );
      }
      overrideValidationSchema(validationSchema);
      setExternalFields((prev) =>
        prev.includes(fieldName) ? prev : [...prev, fieldName]
      );
    }
  };

  const removeFieldFromErrorSchema = (fieldName: string) => {
    if (errorSchema && errorSchema[fieldName]) {
      delete errorSchema[fieldName];
      overrideErrorSchema(errorSchema);
    }
  };

  const handleRule = (superiorValue: string, config: DependencyProps) => {
    // Skip third-party system rules as they are based on dynamic configurations
    if (config.dependencySourceType === DependencySourceType.THIRD_PARTY_SYSTEM)
      return;

    if (config.dependencySourceType === DependencySourceType.URL_PARAMETER) {
      const currentValueFromQueryString = getUrlParameterValue(
        URIParameter,
        config.superiorUrlParameterName
      );
      if (currentValueFromQueryString) {
        superiorValue = currentValueFromQueryString;
      }
    }

    const shouldRenderChildren = evaluateCondition(
      config.condition,
      superiorValue
    );
    shouldRenderChildren
      ? doAction(config.action, superiorValue, config.dependencySourceType)
      : doAction(
          config.actionNegative,
          superiorValue,
          config.dependencySourceType
        );
  };

  useEffect(() => {
    if (rulesConfig && rulesConfig.length > 0 && superiorValue !== undefined) {
      handleRule(superiorValue, rulesConfig[0]);
    }
  }, [superiorValue, rulesConfig]);

  if (!rulesConfig || rulesConfig.length < 1) return <>{children}</>;

  // Avoids setState during render to fix forwardRef(Field) update warning
  const SyncOnEffect = ({ currentValue }: { currentValue: any }) => {
    useEffect(() => {
      if (currentValue !== superiorValue) {
        setSuperiorValue(currentValue);
      }
    }, [currentValue, superiorValue]);

    return null;
  };

  return (
    <Field name={rulesConfig[0].superiorName} subscription={{ value: true }}>
      {({ input: { value } }) => {
        const currentValue =
          rulesConfig[0].dependencySourceType !==
          DependencySourceType.URL_PARAMETER
            ? value
            : rulesConfig[0].superiorUrlParameterName;

        return (
          <>
            {/* Safely sync field value to component-level state */}
            <SyncOnEffect currentValue={currentValue} />
            {shouldRenderChildren ? children : null}
          </>
        );
      }}
    </Field>
  );
};
